import React, { useRef, useEffect, useContext, useState } from "react";
import { Select, MenuItem } from "@mui/material";
import { AppContext } from "../context/AppContext";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import getCategories from "../utils/getCategories";
import formatCategory from "../utils/formatCategory";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY;

const resultOptions = [5, 10, 15, 20];

const geocoderOptions = (placeholder) => ({
  accessToken: mapboxgl.accessToken,
  placeholder,
  // Street addresses plus neighborhoods ("Silver Lake"), cities, zip codes
  // and named places
  types: "address,poi,neighborhood,locality,place,postcode",
  countries: "us",
  // Southern California, so "Venice" means Venice Beach, not Venice, FL
  bbox: [-119.95, 32.5, -116.0, 34.85],
  proximity: {
    longitude: -118.2437,
    latitude: 34.0522,
  },
  limit: 5,
});

const menuItemSx = { fontFamily: '"Plus Jakarta Sans", system-ui', fontSize: 14 };

function Controller() {
  const {
    address1,
    setAddress1,
    address2,
    setAddress2,
    searched,
    setSearched,
    filters,
    setFilters,
    locations,
    resultNum,
    setResultNum,
  } = useContext(AppContext);
  const [categories, setCategories] = useState([]);

  const geocoder1 = useRef(null);
  const geocoder2 = useRef(null);
  const container1 = useRef(null);
  const container2 = useRef(null);

  useEffect(() => {
    const setups = [
      [geocoder1, container1, "Your address or neighborhood", setAddress1],
      [geocoder2, container2, "Their address or neighborhood", setAddress2],
    ];

    setups.forEach(([geocoder, container, placeholder, setAddress]) => {
      geocoder.current = new MapboxGeocoder(geocoderOptions(placeholder));
      container.current.appendChild(geocoder.current.onAdd(null));
      geocoder.current.on("result", (e) => setAddress(e.result));
      // Clearing the text (or the "x" button) removes the address and its pin
      geocoder.current.on("clear", () => setAddress(""));
    });

    return () => {
      setups.forEach(([geocoder]) => geocoder.current?.onRemove());
    };
  }, [setAddress1, setAddress2]);

  useEffect(() => {
    if (locations.length > 0) {
      setCategories(getCategories(locations));
    }
  }, [locations]);

  const getMiddleGround = () => {
    if (!searched) {
      if (address1 && address2) setSearched(true);
      return;
    }

    setSearched(false);
    setFilters([]);
    // clear() also fires "clear", which resets each address. setInput("")
    // would leave the geocoder ignoring the same address if picked again
    geocoder1.current.clear();
    geocoder2.current.clear();
  };

  return (
    <div className="controls">
      <div className="address-fields">
        <label className="address-field">
          <span className="address-field__label">You</span>
          <div className="address-field__input" ref={container1} />
        </label>
        <label className="address-field">
          <span className="address-field__label">Them</span>
          <div className="address-field__input" ref={container2} />
        </label>
      </div>

      <div className="controls__row">
        <Select
          className="pill-select pill-select--filters"
          multiple
          displayEmpty
          value={filters}
          onChange={(e) => setFilters(e.target.value)}
          variant="standard"
          disableUnderline
          inputProps={{ "aria-label": "Filter by category" }}
          renderValue={(selected) =>
            selected.length === 0
              ? "All categories"
              : selected.map(formatCategory).join(", ")
          }
        >
          {categories.map((category) => (
            <MenuItem key={category} value={category} sx={menuItemSx}>
              {formatCategory(category)}
            </MenuItem>
          ))}
        </Select>

        <Select
          className="pill-select pill-select--count"
          value={resultNum}
          onChange={(e) => setResultNum(e.target.value)}
          variant="standard"
          disableUnderline
          inputProps={{ "aria-label": "Number of results" }}
          renderValue={(selected) => `${selected} spots`}
        >
          {resultOptions.map((r) => (
            <MenuItem key={`resultNum-${r}`} value={r} sx={menuItemSx}>
              {r} spots
            </MenuItem>
          ))}
        </Select>
      </div>

      <button
        type="button"
        className={`button ${searched ? "button--secondary" : ""}`}
        onClick={getMiddleGround}
        disabled={!searched && (!address1 || !address2)}
      >
        {searched ? "Start over" : "Find middle ground"}
      </button>
    </div>
  );
}

export default Controller;
